import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { SubscriptionSummary } from '../../../../types/subscription';
import { useSelector } from 'react-redux';
import { selectOverviewChartPercentage } from '../../../../store/slices/ui';
import { SortingToggleOptions } from '../../types';

type ChartsContext = {
  getChartData: (type: SortingToggleOptions) => any;
};

const Context = createContext<ChartsContext>({
  getChartData: () => null,
});

type ChartsContextProps = PropsWithChildren & {
  totalValue: number;
  subscriptions: Array<SubscriptionSummary>;
};

export const OverviewChartsContext = ({
  children,
  totalValue,
  subscriptions,
}: ChartsContextProps) => {
  const isRelative = useSelector(selectOverviewChartPercentage);

  const diversityData = useMemo(
    () =>
      subscriptions
        .map((subscription) => ({
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: subscription.totalBalanceInStable / totalValue,
        }))
        .sort((a, b) => b.y - a.y),
    [subscriptions, totalValue],
  );

  const totalStableProfitData = useMemo(() => {
    return subscriptions
      .map((subscription) => {
        const value =
          (isRelative
            ? subscription.profits?.total.stable.relative
            : subscription.profits?.total.stable.absolute) ?? 0;

        return {
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: Math.abs(value),
          value,
        };
      })
      .filter((subscription) => subscription.y !== 0)
      .sort((a, b) => b.value - a.value);
  }, [isRelative, subscriptions]);

  const totalAssetProfitData = useMemo(() => {
    return subscriptions
      .map((subscription) => {
        const value =
          (isRelative
            ? subscription.profits?.total.asset.relative
            : (subscription.profits?.total.asset.absolute ?? 0) *
              subscription.latestPrice) ?? 0;

        return {
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: Math.abs(value),
          value,
          labelValue: subscription.profits?.total.asset.relative ?? 0,
        };
      })
      .filter((subscription) => subscription.y !== 0)
      .sort((a, b) => b.value - a.value);
  }, [isRelative, subscriptions]);

  const currentStableProfitData = useMemo(() => {
    return subscriptions
      .filter(
        (s) => s.latestTransaction && s.latestTransaction.orderType === 'BUY',
      )
      .map((subscription) => {
        const value = isRelative
          ? subscription.currentProfit ?? 0
          : subscription.profits?.current.stable?.absolute ?? 0;

        return {
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: Math.abs(value),
          value,
        };
      })
      .filter((bot) => bot.y !== 0)
      .sort((a, b) => b.value - a.value);
  }, [isRelative, subscriptions]);

  const currentAssetProfitData = useMemo(() => {
    return subscriptions
      .filter(
        (s) => s.latestTransaction && s.latestTransaction.orderType === 'SELL',
      )
      .map((subscription) => {
        const value =
          (isRelative
            ? subscription.currentProfit
            : (subscription.profits?.current.asset?.absolute ?? 0) *
              subscription.latestPrice) ?? 0;

        return {
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: Math.abs(value),
          value,
          labelValue: subscription.profits?.current.asset?.relative ?? 0,
        };
      })
      .filter((bot) => bot.y !== 0)
      .sort((a, b) => b.value - a.value);
  }, [isRelative, subscriptions]);

  const noAlgorithmicProgressionProfitData = useMemo(() => {
    return subscriptions
      .map((subscription) => {
        const value =
          (isRelative
            ? subscription.profits?.noAlgorithmicProgressionProfit.relative
            : subscription.profits?.noAlgorithmicProgressionProfit.absolute) ??
          0;

        return {
          subscriptionId: subscription.id,
          x: subscription.baseAsset,
          y: Math.abs(value),
          value,
        };
      })
      .filter((subscription) => subscription.y !== 0)
      .sort((a, b) => b.value - a.value);
  }, [isRelative, subscriptions]);

  const getChartData = useCallback(
    (type: SortingToggleOptions) => {
      switch (type) {
        case SortingToggleOptions.Diversity:
        case SortingToggleOptions.TotalValue:
          return diversityData;

        case SortingToggleOptions.TotalStableProfit:
          return totalStableProfitData;

        case SortingToggleOptions.TotalAssetProfit:
          return totalAssetProfitData;

        case SortingToggleOptions.CurrentStableProfit:
          return currentStableProfitData;

        case SortingToggleOptions.CurrentAssetProfit:
          return currentAssetProfitData;

        case SortingToggleOptions.NoAlgorithmicDifference:
          return noAlgorithmicProgressionProfitData;
      }
    },
    [
      currentAssetProfitData,
      currentStableProfitData,
      diversityData,
      noAlgorithmicProgressionProfitData,
      totalAssetProfitData,
      totalStableProfitData,
    ],
  );

  const values = useMemo(
    () => ({
      getChartData,
    }),
    [getChartData],
  );

  return <Context.Provider value={values}>{children}</Context.Provider>;
};

export const useChartsContext = () => useContext(Context);
